import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  REBUILD_LIMIT_TITLE,
  rebuildAtLimit,
  type FieldDebateQuota,
} from "@/lib/fieldDebateQuota";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Jaccard overlap between the map's paper set and the current scope. */
  overlapPercent: number;
  added: number;
  removed: number;
  quota: FieldDebateQuota | undefined;
  onRefresh: () => void;
  onRebuild: () => void;
}

/**
 * Shown instead of firing a refresh blindly when the corpus has drifted
 * past the heavy-drift threshold: the user chooses between slotting new
 * papers into the existing camps (Refresh) and re-deriving the camps
 * (Rebuild) BEFORE anything runs, with the cost of each stated. This is the
 * confirm step for Rebuild — callers must not stack a native confirm on it.
 */
export function FieldDebateRerunChoiceDialog({
  open,
  onOpenChange,
  overlapPercent,
  added,
  removed,
  quota,
  onRefresh,
  onRebuild,
}: Props) {
  const atLimit = rebuildAtLimit(quota);
  const quotaLine =
    quota?.builds_limit != null
      ? ` ${Math.max(quota.builds_limit - quota.builds_used, 0)} of ${quota.builds_limit} rebuilds left this month.`
      : "";
  const papers = (n: number) => `${n} paper${n === 1 ? "" : "s"}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>The camps are from an older paper set</DialogTitle>
          <DialogDescription>
            Only {overlapPercent}% overlap between the papers the camps were
            derived from and the corpus now. Two ways to bring the map up to
            date:
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border border-border p-3">
            <div className="font-medium">Refresh</div>
            <p className="mt-1 text-muted-foreground">
              Slot {added} new {added === 1 ? "paper" : "papers"} into the
              existing camps
              {removed ? ` and drop ${papers(removed)} that left the corpus` : ""}.
              Every current placement stays. Runs in the background and does
              not use a rebuild.
            </p>
          </div>
          <div className="rounded-md border border-border p-3">
            <div className="font-medium">Rebuild</div>
            <p className="mt-1 text-muted-foreground">
              Re-derive the camps from scratch. Placements will shuffle — camp
              structures typically overlap only ~50% between rebuilds.
              {quotaLine}
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              onRebuild();
            }}
            disabled={atLimit}
            title={atLimit ? REBUILD_LIMIT_TITLE : undefined}
          >
            Rebuild (re-derive camps)
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false);
              onRefresh();
            }}
          >
            Refresh (keep camps)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
